import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LayoutGrid, List, Plus, ChevronRight, Loader2, Mail, Pin, PinOff, MessageCircle } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { SectionTitle } from '../../components/ui/SectionTitle';
import { AppCard } from '../../components/ui/AppCard';
import { AvatarView } from '../../components/ui/AvatarView';
import { Button } from '../../components/ui/Button';
import { UnreadBadge } from '../../components/ui/UnreadBadge';
import { Badge } from '../../components/ui/Badge';
import { ConnectionDot } from '../../components/ui/ConnectionDot';
import { IconButton } from '../../components/ui/IconButton';
import { SyncButton } from '../../components/ui/SyncButton';
import { EmptyState } from '../../components/ui/EmptyState';
import { useInfiniteScroll } from '../../lib/useInfiniteScroll';
import {
  searchUsers,
  fetchUserProfileById,
  addChannelMember,
  enableShareLink,
  inviteChannelByEmail,
  type UserProfile,
} from '../../api/dataApi';
import { queryClient, queryKeys } from '../../lib/queryClient';
import { DEFAULT_BUNCHING_INTERVAL, DEFAULT_MEMBER_PERMISSIONS, label as capitalize, type Channel } from '../../types';
import { Layers } from 'lucide-react';

interface UserResult {
  userId: string;
  profile: UserProfile;
}

// Same pattern ChannelShareScreen's "Add by email or @nickname" search uses
// to decide a no-results query is an invitable email rather than a typo.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// A brand-new 1:1 channel between the caller and one other user - a normal
// channel (chat/messages view) with a client-generated, effectively unique
// nickname so it never collides with anything the caller (or the invitee)
// already owns, plus isDirect: true (backend/workflows/channel/create.wsl)
// so the invitee is auto-promoted to co-owner on accept (invite_accept.wsl's
// AddDirectCoOwner) instead of staying an ordinary member - the whole point
// of a DM is that both sides are equals. Otherwise this is the same
// create-then-invite flow ChannelShareScreen's "Add member" already does,
// just triggered from a person hit in this screen's search instead of from
// inside an existing channel.
function directMessageDraft(nickname: string, title: string): Channel {
  return {
    id: '',
    title,
    nickname,
    description: '',
    templateIds: [],
    viewType: 'messages',
    viewChartType: 'bar',
    viewDesign: 'classic',
    chartSettings: {},
    publicChartFiltersAllowed: false,
    isPublic: false,
    accentHex: '#3B82F6',
    avatarSymbol: 'circle-user',
    avatarImageKey: '',
    followers: 0,
    following: false,
    likes: 0,
    liked: false,
    muted: false,
    posts: [],
    workflows: [],
    tags: [],
    ownerIds: [],
    memberPermissions: { ...DEFAULT_MEMBER_PERMISSIONS },
    revealRounds: false,
    bunchingInterval: DEFAULT_BUNCHING_INTERVAL,
    chartCompareChannels: [],
    reportAdditionalChannels: [],
    priority: 0,
    // Marks this as a 1:1 conversation (backend/workflows/channel/
    // create.wsl's isDirect field) - the only effect server-side is that
    // the invited participant is auto-promoted to co-owner once they
    // accept (invite_accept.wsl's AddDirectCoOwner), so both sides of the
    // DM are equal owners instead of an owner + an ordinary member.
    isDirect: true,
  };
}

export function ChannelsListScreen() {
  const {
    channels,
    channelInvites,
    syncChannels,
    loadMoreChannels,
    channelsHasMore,
    unreadByChannel,
    isChannelOwner,
    manualRefresh,
    isChannelPinned,
    setChannelPinned,
    createChannel,
    checkChannelNicknameAvailable,
    profile,
    setErrorMessage,
  } = useStore();
  const navigate = useNavigate();
  const [showGrid, setShowGrid] = useState(false);
  const [query, setQuery] = useState('');
  const [userResults, setUserResults] = useState<UserResult[]>([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [startingDmId, setStartingDmId] = useState<string | null>(null);
  const [invitingEmail, setInvitingEmail] = useState(false);
  const requestId = useRef(0);
  const sentinelRef = useInfiniteScroll(loadMoreChannels, channelsHasMore);

  // The pin toggle lives inside the row's <Link>, so stop the click from
  // navigating.
  const togglePin = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    e.stopPropagation();
    void setChannelPinned(id, !isChannelPinned(id));
  };
  const PinButton = ({ id }: { id: string }) => {
    const pinned = isChannelPinned(id);
    return (
      <button
        type="button"
        onClick={(e) => togglePin(e, id)}
        aria-label={pinned ? 'Unpin channel' : 'Pin channel'}
        className={`shrink-0 rounded-full p-1.5 transition-colors ${
          pinned
            ? 'text-blue-600 dark:text-blue-400'
            : 'text-black/25 dark:text-white/25 hover:text-black/50 dark:hover:text-white/50'
        }`}
      >
        {pinned ? <Pin size={16} className="fill-current" /> : <PinOff size={16} />}
      </button>
    );
  };

  // Refetch the channel list every time this screen is visited - the store
  // otherwise only syncs once per app load (StoreContext.tsx bootstrap
  // effect). syncChannels already forces the network and applies the
  // result non-destructively; its identity is unstable (memoised over
  // `channels`), so run exactly once on mount. Skipped in manual-refresh
  // mode (Settings > Connection) - the Sync button below is shown there
  // instead so the user pulls the list on demand.
  useEffect(() => {
    if (manualRefresh) return;
    void syncChannels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search over accounts (email/nickname) - the same GET
  // /search/user/{term} ChannelShareScreen's "Add member" uses, here
  // surfacing hits directly on the Channels screen so starting a 1:1
  // doesn't require opening/creating a channel first. Only once at least 2
  // characters are typed, matching ChannelShareScreen's own threshold.
  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setUserResults([]);
      setSearchingUsers(false);
      return;
    }
    setSearchingUsers(true);
    const thisRequest = ++requestId.current;
    const timeout = setTimeout(async () => {
      try {
        const hits = await queryClient.query({ queryKey: queryKeys.userSearch(term), queryFn: () => searchUsers(term) });
        const profiles = await Promise.all(hits.map((h) => fetchUserProfileById(h.userId)));
        const results = hits
          .map((hit, i) => (profiles[i] ? { userId: hit.userId, profile: profiles[i] as UserProfile } : null))
          .filter((r): r is UserResult => r !== null && r.userId !== profile.userId);
        if (requestId.current === thisRequest) setUserResults(results);
      } catch {
        if (requestId.current === thisRequest) setUserResults([]);
      } finally {
        if (requestId.current === thisRequest) setSearchingUsers(false);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [query, profile.userId]);

  // @dm-<their nickname>, stripped of the leading @ and anything not
  // alphanumeric/dash/underscore (a display nickname can contain
  // characters a channel nickname can't). Falls back to a numeric suffix
  // only on an actual collision (checkChannelNicknameAvailable) - the
  // common case (one DM per pair of people) never needs one. Bounded at
  // 20 attempts so a pathological run of collisions can't loop forever.
  const dmNicknameFor = async (theirNickname: string): Promise<string> => {
    const base = 'dm-' + theirNickname.replace(/^@/, '').replace(/[^a-zA-Z0-9_-]/g, '').toLowerCase();
    for (let n = 0; n < 20; n++) {
      const candidate = `@${base}${n === 0 ? '' : `-${n + 1}`}`;
      if (await checkChannelNicknameAvailable(candidate)) return candidate;
    }
    return `@dm-${profile.userId.slice(0, 8)}-${Date.now().toString(36)}`;
  };

  const startDirectMessage = async (result: UserResult) => {
    setStartingDmId(result.userId);
    try {
      const displayName = result.profile.name.fullname || result.profile.name.name || result.profile.nickname;
      const nickname = await dmNicknameFor(result.profile.nickname);
      await createChannel(directMessageDraft(nickname, displayName));
      await addChannelMember(nickname, {
        userId: result.userId,
        email: result.profile.personal.email,
        nickname: result.profile.nickname,
        name: displayName,
        avatarSymbol: 'person.crop.circle',
      });
      setQuery('');
      navigate(`/channels/${nickname}`);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Unable to start conversation.');
    } finally {
      setStartingDmId(null);
    }
  };

  // No account matches this email - offer the same email-invite flow
  // ChannelShareScreen's "Add by email or @nickname" search falls back to
  // (see inviteChannelByEmail's comment in dataApi.ts). There's no existing
  // channel to invite into here, so this first creates the same 1:1 draft
  // startDirectMessage would, then sends the invite against it instead of
  // adding an existing member.
  const inviteByEmail = async (email: string) => {
    setInvitingEmail(true);
    try {
      const nickname = await dmNicknameFor(email.split('@')[0]);
      await createChannel(directMessageDraft(nickname, email));
      await enableShareLink(nickname);
      await inviteChannelByEmail(nickname, email);
      setQuery('');
      navigate(`/channels/${nickname}`);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Unable to send the invite.');
    } finally {
      setInvitingEmail(false);
    }
  };

  const q = query.trim().toLowerCase();
  const filteredChannels = q
    ? channels.filter(
        (c) => c.title.toLowerCase().includes(q) || c.nickname.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q)
      )
    : channels;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <SectionTitle title="Channels" subtitle="Your live spaces" />
        <div className="flex-1" />
        {manualRefresh && <SyncButton onSync={syncChannels} ariaLabel="Sync channels" />}
        {channelInvites.length > 0 && (
          <Link to="/channels/invites" className="relative" aria-label={`${channelInvites.length} pending invitation${channelInvites.length === 1 ? '' : 's'}`}>
            <IconButton icon={Mail} />
            <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
              {channelInvites.length}
            </span>
          </Link>
        )}
        <IconButton icon={showGrid ? List : LayoutGrid} onClick={() => setShowGrid((v) => !v)} />
        <Link to="/channels/new">
          <IconButton icon={Plus} variant="filled" />
        </Link>
      </div>

      <input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search your channels or find someone to message"
        className="rounded-xl border border-black/15 dark:border-white/15 bg-white dark:bg-white/5 px-3.5 py-2.5 text-[15px] outline-none focus:border-blue-500"
      />

      {query.trim().length >= 2 && (
        <div className="flex flex-col gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-black/40 dark:text-white/40">People</span>
          {searchingUsers && <span className="text-sm text-black/40 dark:text-white/40">Searching...</span>}
          {!searchingUsers && userResults.length === 0 && EMAIL_PATTERN.test(query.trim()) && (
            <AppCard className="flex flex-col gap-3">
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="font-semibold truncate">No account with this email yet</span>
                <span className="text-sm text-black/50 dark:text-white/50 truncate">{query.trim()}</span>
              </div>
              <Button onClick={() => void inviteByEmail(query.trim())} disabled={invitingEmail}>
                {invitingEmail ? 'Sending...' : 'Invite by email'}
              </Button>
            </AppCard>
          )}
          {!searchingUsers && userResults.length === 0 && !EMAIL_PATTERN.test(query.trim()) && (
            <span className="text-sm text-black/40 dark:text-white/40">No matching accounts.</span>
          )}
          {userResults.map((result) => (
            <button
              key={result.userId}
              type="button"
              onClick={() => startDirectMessage(result)}
              disabled={startingDmId !== null}
              className="flex items-center gap-3 text-left w-full disabled:opacity-60"
            >
              <AppCard className="flex items-center gap-3 w-full">
                <AvatarView symbol="circle-user" colorHex="#3B82F6" size={44} />
                <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                  <span className="font-semibold truncate">{result.profile.name.fullname || result.profile.name.name || result.profile.nickname}</span>
                  <span className="text-sm text-black/50 dark:text-white/50 truncate">{result.profile.nickname}</span>
                </div>
                {startingDmId === result.userId ? (
                  <Loader2 size={18} className="animate-spin text-black/30 dark:text-white/30 shrink-0" />
                ) : (
                  <MessageCircle size={18} className="text-black/30 dark:text-white/30 shrink-0" />
                )}
              </AppCard>
            </button>
          ))}
        </div>
      )}

      {filteredChannels.length === 0 ? (
        query.trim() ? (
          <EmptyState icon={Layers} title="No channels match" subtitle="Try a different search, or message a person above." />
        ) : (
          <EmptyState icon={Layers} title="No channels" subtitle="Create your first channel to get started." />
        )
      ) : showGrid ? (
        <div className="grid gap-3 grid-cols-2 sm:grid-cols-3">
          {filteredChannels?.map((channel) => (
            <Link key={channel.id} to={`/channels/${channel.id}`}>
              <AppCard className="flex flex-col gap-3 h-full">
                <div className="flex items-start justify-between">
                  <span className="relative shrink-0">
                    <AvatarView symbol={channel.avatarSymbol} colorHex={channel.accentHex} imageKey={channel.avatarImageKey} size={56} />
                  </span>
                  <div className="flex items-center gap-1">
                    <UnreadBadge count={unreadByChannel[channel.nickname] ?? 0} />
                    <ConnectionDot channelId={channel.id} />
                    <PinButton id={channel.id} />
                  </div>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="font-semibold">{channel.title}</span>
                  <span className="flex items-center gap-1.5 text-sm text-black/50 dark:text-white/50">
                    <span className="truncate">{channel.nickname}</span>
                    {isChannelOwner(channel) && <Badge className="shrink-0">Owner</Badge>}
                  </span>
                </div>
                <p className="text-xs text-black/50 dark:text-white/50 line-clamp-2 m-0">{channel.description}</p>
              </AppCard>
            </Link>
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filteredChannels?.map((channel) => (
            <Link key={channel.id} to={`/channels/${channel.id}`}>
              <AppCard className="flex items-center gap-3">
                <span className="relative shrink-0">
                  <AvatarView symbol={channel.avatarSymbol} colorHex={channel.accentHex} imageKey={channel.avatarImageKey} size={52} />
                </span>
                <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="font-semibold truncate">{channel.title}</span>
                    {isChannelOwner(channel) && <Badge className="shrink-0">Owner</Badge>}
                  </span>
                  <span className="text-sm text-black/50 dark:text-white/50 truncate">{channel.nickname}</span>
                  <span className="text-xs text-black/40 dark:text-white/40">{capitalize(channel.viewType)}</span>
                </div>
                <UnreadBadge count={unreadByChannel[channel.nickname] ?? 0} className="shrink-0" />
                <ConnectionDot channelId={channel.id} />
                <PinButton id={channel.id} />
                <ChevronRight size={18} className="text-black/30 dark:text-white/30 shrink-0" />
              </AppCard>
            </Link>
          ))}
        </div>
      )}

      {!query.trim() && channels.length > 0 && channelsHasMore && (
        <div ref={sentinelRef} className="flex justify-center py-4">
          <Loader2 size={20} className="animate-spin text-black/30 dark:text-white/30" />
        </div>
      )}
    </div>
  );
}
